/**
 * Full Experience Animation Loop Hook (Web)
 *
 * Extracted from FullExperienceAnimation for <300L compliance.
 * Contains the main RAF animation loop and state management
 * for the 5-phase animation system.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Animated } from 'react-native';
import { useReducedMotionFallback } from './hooks/useReducedMotionFallback';
import { criticalLog } from '../../lib/debug-logger';
import type { AnimationPhase, SpeedSettings, AnalysisResultForAnimation } from './types';
import {
  PHASE_TIMING, CONVERGENCE_CONSTANTS, REVELATION_CONSTANTS, NARRATOR_SUBTITLE_CONSTANTS,
} from './types';
import {
  SCREEN_WIDTH, getConvergenceSubPhase, calculateLetterPositions,
  splitIntoLines, updateConvergingLetter, buildFormedTextFromPositions,
  animateBadgeReveal,
  type ConvergingLetterState,
} from './animationHelpers.web';

interface LoopParams {
  text: string;
  speed: string;
  settings: SpeedSettings;
  wordsForLine: number;
  analysisResult: AnalysisResultForAnimation | undefined;
  narratorQuip: string | null;
  onComplete?: () => void;
  onSkip?: () => void;
  badgeScale: Animated.Value;
  badgeOpacity: Animated.Value;
  textOpacity: Animated.Value;
  totalDurationBase: number;
  lineHeight: number;
  paddingLines: number;
}

export function useFullExperienceAnimationLoop(params: LoopParams) {
  const {
    text, settings, wordsForLine, analysisResult, narratorQuip,
    onComplete, onSkip, badgeScale, badgeOpacity, textOpacity,
    totalDurationBase, lineHeight, paddingLines,
  } = params;

  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [subPhase, setSubPhase] = useState<'chaos' | 'converging' | 'lineForming' | 'transitioning'>('chaos');
  const [showSkipHint, setShowSkipHint] = useState(false);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [convergingLetters, setConvergingLetters] = useState<ConvergingLetterState[]>([]);
  const [lineComplete, setLineComplete] = useState(false);
  const [formedText, setFormedText] = useState('');
  const [showNarratorSubtitle, setShowNarratorSubtitle] = useState(false);
  const narratorSubtitleShownRef = useRef(false);
  const [readingLines, setReadingLines] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [readingScrollY, setReadingScrollY] = useState(0);
  const [convergenceScale, setConvergenceScale] = useState(1);
  const [convergenceBlurProgress, setConvergenceBlurProgress] = useState(0);

  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const skipRequestedRef = useRef(false);
  const letterPositionsRef = useRef<ReturnType<typeof calculateLetterPositions>>([]);
  const phaseRef = useRef<AnimationPhase>('idle');
  const lineCompleteRef = useRef(false);
  const readingLinesRef = useRef<string[]>([]);
  const analysisResultRef = useRef(analysisResult);
  const badgeDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefersReducedMotion = useReducedMotionFallback();

  const totalDuration = useMemo(() => totalDurationBase / settings.multiplier, [settings.multiplier, totalDurationBase]);

  useEffect(() => {
    const lines = splitIntoLines(text, 50);
    const dots = Array(paddingLines).fill('· · ·');
    const padded = [...dots, ...lines, ...dots];
    setReadingLines(padded);
    readingLinesRef.current = padded;
  }, [text, paddingLines]);

  useEffect(() => { analysisResultRef.current = analysisResult; }, [analysisResult]);

  useEffect(() => {
    const cw = SCREEN_WIDTH * 0.8;
    letterPositionsRef.current = calculateLetterPositions(text, cw, wordsForLine);
    setConvergingLetters(letterPositionsRef.current.map((pos, idx) => ({
      ...pos, globalIndex: idx,
      startX: (Math.random() - 0.5) * SCREEN_WIDTH * 1.5,
      startY: (Math.random() - 0.5) * 400,
      currentX: (Math.random() - 0.5) * SCREEN_WIDTH * 1.5,
      currentY: (Math.random() - 0.5) * 400,
      scale: 40 + Math.random() * 20, opacity: 0.2 + Math.random() * 0.3,
      chromaOffset: settings.chromaOffset * (1 + Math.random()), landed: false,
    })));
  }, [text, wordsForLine, settings.chromaOffset]);

  // Main animation loop
  useEffect(() => {
    if (prefersReducedMotion) {
      setFormedText(letterPositionsRef.current.map(l => l.char).join(''));
      setLineComplete(true); setProgress(1);
      criticalLog('button', 'showContinueButton set to TRUE (prefersReducedMotion)', { source: 'prefersReducedMotion' });
      setShowContinueButton(true);
      return;
    }

    startTimeRef.current = performance.now();
    setPhase('emergence'); phaseRef.current = 'emergence';

    const animate = (currentTime: number) => {
      if (skipRequestedRef.current) return;
      const elapsed = currentTime - startTimeRef.current;
      const np = Math.min(elapsed / totalDuration, 1);
      setProgress(np);

      const eEnd = PHASE_TIMING.emergence;
      const tEnd = eEnd + PHASE_TIMING.transition;
      const rEnd = tEnd + PHASE_TIMING.reading;
      const cEnd = rEnd + PHASE_TIMING.convergence;

      if (np < eEnd) {
        if (phaseRef.current !== 'emergence') { setPhase('emergence'); phaseRef.current = 'emergence'; }
        const ep = np / eEnd;
        const csp = getConvergenceSubPhase(ep);
        setSubPhase(csp);

        const { EMERGENCE_SHOW_START, EMERGENCE_SHOW_END } = NARRATOR_SUBTITLE_CONSTANTS;
        if (ep >= EMERGENCE_SHOW_START && ep < EMERGENCE_SHOW_END && !narratorSubtitleShownRef.current && narratorQuip) {
          setShowNarratorSubtitle(true); narratorSubtitleShownRef.current = true;
        } else if (ep >= EMERGENCE_SHOW_END) { setShowNarratorSubtitle(false); }

        setConvergingLetters(prev => prev.map((letter, idx) => {
          const tp = letterPositionsRef.current[idx];
          return tp ? updateConvergingLetter(letter, tp, csp, ep, settings.chromaOffset) : letter;
        }));

        if ((csp === 'lineForming' || csp === 'transitioning') && !lineCompleteRef.current) {
          setLineComplete(true); lineCompleteRef.current = true;
          setFormedText(buildFormedTextFromPositions(letterPositionsRef.current));
        }
      } else if (np < tEnd) {
        const wasTransition = phaseRef.current === 'transition';
        if (!wasTransition) { setPhase('transition'); phaseRef.current = 'transition'; }
        if (!lineCompleteRef.current) { setLineComplete(true); lineCompleteRef.current = true; setFormedText(buildFormedTextFromPositions(letterPositionsRef.current)); }
        // Set opacity directly to avoid Animated.timing pileup (called every frame)
        textOpacity.setValue(1 - ((np - eEnd) / PHASE_TIMING.transition) * 0.3);
      } else if (np < rEnd) {
        if (phaseRef.current !== 'reading') { setPhase('reading'); phaseRef.current = 'reading'; }
        const rp = (np - tEnd) / PHASE_TIMING.reading;
        const ac = readingLinesRef.current.length - (paddingLines * 2);
        const tl = Math.min(Math.floor(rp * ac), ac - 1);
        setReadingScrollY(-((tl + paddingLines) * lineHeight + lineHeight / 2));
        setCurrentLineIndex(tl + paddingLines);
      } else if (np < cEnd) {
        if (phaseRef.current !== 'convergence') { setPhase('convergence'); phaseRef.current = 'convergence'; }
        const cp = (np - rEnd) / PHASE_TIMING.convergence;
        const eased = 1 - Math.pow(1 - cp, 2);
        setConvergenceScale(1 - eased * CONVERGENCE_CONSTANTS.SCALE_REDUCTION);
        const ac = readingLinesRef.current.length - (paddingLines * 2);
        setReadingScrollY(-((Math.floor(ac / 2) + paddingLines) * lineHeight + lineHeight / 2));
        setConvergenceBlurProgress(eased);
        // Set opacity directly to avoid Animated.timing pileup (called every frame)
        textOpacity.setValue(CONVERGENCE_CONSTANTS.START_OPACITY + eased * (CONVERGENCE_CONSTANTS.END_OPACITY - CONVERGENCE_CONSTANTS.START_OPACITY));
      } else {
        if (phaseRef.current !== 'complete') {
          setPhase('complete'); phaseRef.current = 'complete';
          Animated.timing(textOpacity, { toValue: REVELATION_CONSTANTS.TEXT_BACKDROP_OPACITY, duration: REVELATION_CONSTANTS.TEXT_DIM_DURATION_MS, useNativeDriver: true }).start();
          if (analysisResultRef.current) {
            // Clear any existing timer before setting a new one (prevents double badge animation)
            if (badgeDelayTimerRef.current) clearTimeout(badgeDelayTimerRef.current);
            badgeDelayTimerRef.current = setTimeout(() => {
              animateBadgeReveal(badgeScale, badgeOpacity).start();
            }, REVELATION_CONSTANTS.BADGE_DELAY_MS);
          }
        }
      }

      if (np < 1) { rafIdRef.current = requestAnimationFrame(animate); }
      else {
        criticalLog('button', 'showContinueButton set to TRUE (animation complete)', { source: 'animation_complete', progress: np });
        setShowContinueButton(true);
      }
    };

    rafIdRef.current = requestAnimationFrame(animate);
    const ht = setTimeout(() => setShowSkipHint(true), 1000);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      clearTimeout(ht);
      if (badgeDelayTimerRef.current) clearTimeout(badgeDelayTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDuration, text.length]);

  const handleSkip = useCallback(() => {
    skipRequestedRef.current = true;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    if (badgeDelayTimerRef.current) clearTimeout(badgeDelayTimerRef.current);
    setFormedText(buildFormedTextFromPositions(letterPositionsRef.current));
    setLineComplete(true); lineCompleteRef.current = true;
    setProgress(1); setPhase('complete'); phaseRef.current = 'complete';
    criticalLog('button', 'showContinueButton set to TRUE (handleSkip)', { source: 'handleSkip' });
    setShowContinueButton(true);
    if (analysisResultRef.current) {
      animateBadgeReveal(badgeScale, badgeOpacity).start();
    }
    onSkip?.();
  }, [badgeScale, badgeOpacity, onSkip]);

  const handleContinue = useCallback(() => {
    criticalLog('button', 'handleContinue called', { hasOnComplete: !!onComplete });
    onComplete?.();
  }, [onComplete]);

  return {
    progress, phase, subPhase, showSkipHint, showContinueButton,
    convergingLetters, lineComplete, formedText, showNarratorSubtitle,
    readingLines, currentLineIndex, readingScrollY,
    convergenceScale, convergenceBlurProgress,
    handleSkip, handleContinue,
  };
}
